import { Marked } from 'marked';
import escape from 'lodash-es/escape.js';

/**
 * Markdown constructs clear enough to turn pasted text into formatting. Prose with a stray `*`, `_` or `#` matches
 * none of them. `__bold__` is left out, so `__init__` alone is not Markdown; `> quote` and `---` are left out
 * because Kerika keeps them as text, so on their own there is nothing to convert.
 */
const MARKDOWN_CONSTRUCTS = [
  /^ {0,3}#{1,6}[ \t]+\S/m, // # Heading
  /^ {0,3}[-*+][ \t]+\S/m, // - bullet
  /^ {0,3}\d+[.)][ \t]+\S/m, // 1. numbered
  /^ {0,3}(```|~~~)/m, // ``` fenced code
  /^ {0,3}\|.*\|[ \t]*$/m, // | table | row |
  /\[[^\]\n]*\]\([^()\s]+\)/, // [text](url)
  /\*\*(?=\S)[\s\S]*?\S\*\*/, // **bold**
  /~~(?=\S)[\s\S]*?\S~~/, // ~~strike~~
  /`[^`\n]+`/, // `keyword`
];

/** Whether pasted plain text carries Markdown worth turning into formatting. */
export const hasMarkdownFormatting = (text) => !!text?.trim() && MARKDOWN_CONSTRUCTS.some((construct) => construct.test(text));

// What Kerika has no format for stays as its text: a quote, a rule (and the `---` under a line, which Markdown
// reads as a heading), an image, a task's checkbox.
const marked = new Marked({
  breaks: true,
  tokenizer: {
    blockquote: () => undefined,
    hr: () => undefined,
    lheading: () => undefined,
  },
  renderer: {
    image: ({ raw }) => escape(raw),
    checkbox: ({ checked }) => (checked ? '[x] ' : '[ ] '),
    // As marked's own, without the newline it leaves at the code's end.
    code: ({ text, lang }) => `<pre><code${lang ? ` class="language-${escape(lang)}"` : ''}>${escape(text)}</code></pre>`,
  },
});

/** Markdown as HTML, at once: a paste has to decide while it is handled. */
export const markdownToHtml = (markdown) => (markdown ? marked.parse(markdown, { async: false }) : '');
